import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Badge from '../../components/ui/Badge';
import Input from '../../components/ui/Input';
import Pagination from '../../components/ui/Pagination';
import { SessionListSkeleton } from '../../components/ui/LoadingSkeleton';
import { participantService } from '../../services';
import { getActivityIcon, getActivityTypeColor } from '../../utils/activityIcons';

/**
 * Activity History Page
 * Shows participant's past activities and responses across all sessions
 */

const ActivityHistoryPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [filterType, setFilterType] = useState('all'); // all, poll, quiz, wordCloud, qa, feedback
  const [filterSession, setFilterSession] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sessions, setSessions] = useState([]);
  const [stats, setStats] = useState({ total: 0, typeCounts: {} });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 20;

  useEffect(() => {
    fetchActivityHistory();
  }, [currentPage, filterType]);

  const fetchActivityHistory = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit: itemsPerPage,
      };

      // Add type filter if not 'all'
      if (filterType !== 'all') {
        params.type = filterType;
      }

      // Add session filter if not 'all'
      if (filterSession !== 'all') {
        params.sessionId = filterSession;
      }

      const response = await participantService.getActivityHistory(params);

      if (response.success) {
        // Transform activities from API
        const transformedActivities = (response.data || []).map((activity) => ({
          id: activity.id,
          sessionId: activity.sessionId,
          sessionName: activity.sessionName,
          sessionCode: activity.sessionCode,
          organizationName: activity.organizationName,
          type: activity.type,
          title: activity.title,
          description: activity.question || '',
          completedAt: activity.submittedAt,
          question: activity.question,
          yourAnswer: formatResponse(activity.response, activity.type),
          response: activity.response,
        }));

        setActivities(transformedActivities);
        setStats(response.stats || { total: 0, typeCounts: {} });
        setTotalPages(response.pagination?.pages || 1);
        setTotalItems(response.total || 0);

        // Extract unique sessions for filter
        const uniqueSessions = [...new Map(
          transformedActivities.map((a) => [a.sessionId, { id: a.sessionId, name: a.sessionName }])
        ).values()];
        setSessions(uniqueSessions);
      }
    } catch (error) {
      console.error('Error fetching activity history:', error);
    } finally {
      setLoading(false);
    }
  };

  // Format response based on activity type
  const formatResponse = (response, type) => {
    if (!response) return 'No response';

    switch (type) {
      case 'poll':
        return response.selectedOption || response.answer || JSON.stringify(response);
      case 'quiz':
        return response.answer || response.selectedOption || JSON.stringify(response);
      case 'wordCloud':
        return response.word || response.words?.join(', ') || JSON.stringify(response);
      case 'qa':
        return response.question || response.answer || JSON.stringify(response);
      case 'feedback':
        return response.rating ? `Rating: ${response.rating}/5 - ${response.comment || ''}` : JSON.stringify(response);
      case 'openQuestion':
        return response.answer || response.text || JSON.stringify(response);
      default:
        return typeof response === 'string' ? response : JSON.stringify(response);
    }
  };

  // Filter activities by search query (client-side filtering for search)
  const filteredActivities = activities.filter((activity) => {
    const matchesSession = filterSession === 'all' || activity.sessionId === filterSession;
    const matchesSearch =
      searchQuery === '' ||
      activity.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      activity.sessionName?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesSession && matchesSearch;
  });

  // Get stats from API response
  const displayStats = {
    total: stats.total || totalItems,
    quizzes: stats.typeCounts?.quiz || 0,
    polls: stats.typeCounts?.poll || 0,
    wordClouds: stats.typeCounts?.wordCloud || 0,
    feedback: stats.typeCounts?.feedback || 0,
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'quiz': return 'primary';
      case 'poll': return 'info';
      case 'reflection': return 'warning';
      case 'survey': return 'success';
      default: return 'neutral';
    }
  };

  return (
    <DashboardLayout title="Activity History">
      <div className="p-6 space-y-6">
        {/* Back Button */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/participant/dashboard')}
            className="inline-flex items-center gap-2 text-neutral-600 hover:text-neutral-900 transition-colors mb-4 group"
          >
            <svg className="w-5 h-5 group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            <span className="font-medium">Back to Dashboard</span>
          </button>
        </div>

        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-neutral-900">Activity History</h1>
          <p className="text-neutral-600 mt-1">
            Review all your past activities and responses
          </p>
        </div>

        {/* Stats Cards */}
        {!loading && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <Card padding="md" hoverable>
              <p className="text-sm font-medium text-neutral-600 mb-1">Total Activities</p>
              <p className="text-2xl font-bold text-neutral-900">{displayStats.total}</p>
            </Card>
            <Card padding="md" hoverable>
              <p className="text-sm font-medium text-neutral-600 mb-1">Quizzes</p>
              <p className="text-2xl font-bold text-primary-600">{displayStats.quizzes}</p>
            </Card>
            <Card padding="md" hoverable>
              <p className="text-sm font-medium text-neutral-600 mb-1">Polls</p>
              <p className="text-2xl font-bold text-blue-600">{displayStats.polls}</p>
            </Card>
            <Card padding="md" hoverable>
              <p className="text-sm font-medium text-neutral-600 mb-1">Word Clouds</p>
              <p className="text-2xl font-bold text-amber-600">{displayStats.wordClouds}</p>
            </Card>
            <Card padding="md" hoverable>
              <p className="text-sm font-medium text-neutral-600 mb-1">Feedback</p>
              <p className="text-2xl font-bold text-green-600">{displayStats.feedback}</p>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card padding="lg">
          <div className="space-y-4">
            {/* Search */}
            <Input
              type="text"
              placeholder="Search activities..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={
                <svg className="w-5 h-5 text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              }
            />

            {/* Filter Buttons */}
            <div className="flex flex-wrap gap-2">
              <p className="text-sm font-medium text-neutral-700 self-center mr-2">Filter by Type:</p>
              <Button
                variant={filterType === 'all' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => { setFilterType('all'); setCurrentPage(1); }}
              >
                All ({displayStats.total})
              </Button>
              <Button
                variant={filterType === 'quiz' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => { setFilterType('quiz'); setCurrentPage(1); }}
              >
                Quizzes ({displayStats.quizzes})
              </Button>
              <Button
                variant={filterType === 'poll' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => { setFilterType('poll'); setCurrentPage(1); }}
              >
                Polls ({displayStats.polls})
              </Button>
              <Button
                variant={filterType === 'wordCloud' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => { setFilterType('wordCloud'); setCurrentPage(1); }}
              >
                Word Clouds ({displayStats.wordClouds})
              </Button>
              <Button
                variant={filterType === 'feedback' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => { setFilterType('feedback'); setCurrentPage(1); }}
              >
                Feedback ({displayStats.feedback})
              </Button>
            </div>

            {/* Session Filter */}
            <div className="flex flex-wrap gap-2">
              <p className="text-sm font-medium text-neutral-700 self-center mr-2">Filter by Session:</p>
              <Button
                variant={filterSession === 'all' ? 'primary' : 'ghost'}
                size="sm"
                onClick={() => setFilterSession('all')}
              >
                All Sessions
              </Button>
              {sessions.map((session) => (
                <Button
                  key={session.id}
                  variant={filterSession === session.id ? 'primary' : 'ghost'}
                  size="sm"
                  onClick={() => setFilterSession(session.id)}
                >
                  {session.name}
                </Button>
              ))}
            </div>
          </div>
        </Card>

        {/* Activities List */}
        {loading ? (
          <SessionListSkeleton count={5} />
        ) : filteredActivities.length === 0 ? (
          <Card padding="xl">
            <div className="text-center py-12">
              <svg className="w-20 h-20 mx-auto text-neutral-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <h3 className="text-lg font-semibold text-neutral-900 mb-2">No Activities Found</h3>
              <p className="text-neutral-600 mb-6">
                {searchQuery || filterType !== 'all' || filterSession !== 'all'
                  ? 'Try adjusting your filters or search query'
                  : 'Complete activities in sessions to see them here'}
              </p>
              {(searchQuery || filterType !== 'all' || filterSession !== 'all') && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchQuery('');
                    setFilterType('all');
                    setFilterSession('all');
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredActivities.map((activity) => (
              <motion.div
                key={activity.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card padding="lg" hoverable>
                  <div className="space-y-4">
                    {/* Header */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${getActivityTypeColor(activity.type)}`}>
                          {getActivityIcon(activity.type, 'md')}
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-neutral-900">{activity.title}</h3>
                          <p className="text-sm text-neutral-600">{activity.description}</p>
                          <div className="flex items-center gap-3 mt-2">
                            <Badge variant="outline" size="sm">
                              {activity.sessionName}
                            </Badge>
                            <Badge variant={getTypeColor(activity.type)} size="sm">
                              {activity.type}
                            </Badge>
                            <span className="text-xs text-neutral-500">
                              {new Date(activity.completedAt).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      {activity.score !== undefined && (
                        <Badge variant={activity.score === activity.maxScore ? 'success' : 'info'} size="lg">
                          {activity.score}/{activity.maxScore}
                        </Badge>
                      )}
                    </div>

                    {/* Content Based on Type */}
                    <div className="pl-13 space-y-3">
                      {/* Quiz */}
                      {activity.type === 'quiz' && (
                        <div className="bg-neutral-50 rounded-lg p-4 space-y-3">
                          <div>
                            <p className="text-sm font-medium text-neutral-700 mb-2">Question:</p>
                            <p className="text-neutral-900">{activity.question}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-neutral-700 mb-2">Your Answer:</p>
                            <p className="text-neutral-900">{activity.yourAnswer}</p>
                          </div>
                          {activity.feedback && (
                            <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-green-900">Feedback:</p>
                              <p className="text-sm text-green-700">{activity.feedback}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Poll */}
                      {activity.type === 'poll' && (
                        <div className="bg-neutral-50 rounded-lg p-4 space-y-3">
                          <div>
                            <p className="text-sm font-medium text-neutral-700 mb-2">Question:</p>
                            <p className="text-neutral-900">{activity.question}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-neutral-700 mb-2">Your Answer:</p>
                            <Badge variant="info">{activity.yourAnswer}</Badge>
                          </div>
                          {activity.pollResults && (
                            <div>
                              <p className="text-sm font-medium text-neutral-700 mb-2">Poll Results:</p>
                              <div className="space-y-2">
                                {Object.entries(activity.pollResults).map(([option, count]) => {
                                  const total = Object.values(activity.pollResults).reduce((a, b) => a + b, 0);
                                  const percentage = ((count / total) * 100).toFixed(0);
                                  return (
                                    <div key={option} className="space-y-1">
                                      <div className="flex justify-between text-sm">
                                        <span className="text-neutral-700">{option}</span>
                                        <span className="text-neutral-600">{count} ({percentage}%)</span>
                                      </div>
                                      <div className="w-full bg-neutral-200 rounded-full h-2">
                                        <div
                                          className="bg-blue-600 h-2 rounded-full"
                                          style={{ width: `${percentage}%` }}
                                        ></div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Reflection */}
                      {activity.type === 'reflection' && (
                        <div className="bg-neutral-50 rounded-lg p-4 space-y-3">
                          <div>
                            <p className="text-sm font-medium text-neutral-700 mb-2">Prompt:</p>
                            <p className="text-neutral-900">{activity.question}</p>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-neutral-700 mb-2">Your Reflection:</p>
                            <p className="text-neutral-900 whitespace-pre-wrap">{activity.yourAnswer}</p>
                          </div>
                          {activity.feedback && (
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                              <p className="text-sm font-medium text-amber-900">Trainer Feedback:</p>
                              <p className="text-sm text-amber-700">{activity.feedback}</p>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Survey */}
                      {activity.type === 'survey' && activity.questions && (
                        <div className="bg-neutral-50 rounded-lg p-4 space-y-3">
                          {activity.questions.map((q, index) => (
                            <div key={index} className="pb-3 border-b border-neutral-200 last:border-0 last:pb-0">
                              <p className="text-sm font-medium text-neutral-700 mb-1">{q.question}</p>
                              <p className="text-neutral-900">{q.answer}</p>
                              {q.scale && (
                                <p className="text-xs text-neutral-500 mt-1">Scale: {q.scale}</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {!loading && totalItems > itemsPerPage && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              itemsPerPage={itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ActivityHistoryPage;
